/**
 * Local Code Lite — minimalistická UI pro Qwen Code agenta (port 7691).
 *
 * Jedna stránka:
 *  - Horní lišta: status + Start/Stop + ozubené kolečko (Settings modal)
 *  - Pod tím: ttyd iframe terminál
 *
 * Backend: module-owned Local Code Lite MCP tools (status, config, Qwen lifecycle).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Loader2, Play, Power, RefreshCw, Settings2, Square, Terminal } from 'lucide-react';
import { mcpCall } from '../../lib/mcp';
import { Button, Card, CardContent, Modal, StatusDot } from '../../components/ui';
import { toast } from 'sonner';
import { themedConfirm } from '../../lib/themed-dialogs';

interface QwenStatus {
  session: boolean;
  ttyd: boolean;
  running: boolean;
  command: string;
  port: number;
}

interface StatusResp {
  ok: boolean;
  qwenCode: QwenStatus;
}

interface ConfigResp {
  ok: boolean;
  config: { qwen_api_key?: string; qwen_api_base?: string; qwen_model?: string };
}

export default function LocalCodeLitePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const statusQ = useQuery({
    queryKey: ['localcode-lite-status'],
    queryFn: () => mcpCall<StatusResp>('localcode-lite.status_get', {}, { silent: true }),
    refetchInterval: 5_000,
  });
  const status = statusQ.data?.qwenCode;

  const startMut = useMutation({
    mutationFn: () => mcpCall('localcode-lite.qwen_start'),
    onSuccess: () => {
      toast.success(t('localcodeLite.sessionStarted'));
      qc.invalidateQueries({ queryKey: ['localcode-lite-status'] });
      // Po 2s + 5s re-check (running state se objeví až qwen vyleze přes bash)
      setTimeout(() => qc.invalidateQueries({ queryKey: ['localcode-lite-status'] }), 2000);
      setTimeout(() => qc.invalidateQueries({ queryKey: ['localcode-lite-status'] }), 5000);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const stopMut = useMutation({
    mutationFn: () => mcpCall('localcode-lite.qwen_stop'),
    onSuccess: () => {
      toast.success(t('localcodeLite.sessionStopped'));
      qc.invalidateQueries({ queryKey: ['localcode-lite-status'] });
      setTimeout(() => qc.invalidateQueries({ queryKey: ['localcode-lite-status'] }), 2000);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const killMut = useMutation({
    mutationFn: () => mcpCall('localcode-lite.qwen_kill'),
    onSuccess: () => {
      toast.success(t('localcodeLite.sessionKilled'));
      qc.invalidateQueries({ queryKey: ['localcode-lite-status'] });
      setTimeout(() => qc.invalidateQueries({ queryKey: ['localcode-lite-status'] }), 2000);
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className='flex flex-col gap-3 h-full'>
      {/* Horní lišta — status + ovládání */}
      <Card>
        <CardContent className='py-3'>
          <div className='flex items-center justify-between gap-4 flex-wrap'>
            <div className='flex items-center gap-5 text-xs'>
              <div className='flex items-center gap-2'>
                <Terminal size={14} className='text-emerald-400' />
                <span className='text-sm font-semibold text-white'>Qwen Code</span>
              </div>

              {statusQ.isLoading
                ? (
                  <span className='flex items-center gap-2 text-gray-500'>
                    <Loader2 size={12} className='animate-spin' /> {t('common.loading')}
                  </span>
                )
                : status
                ? (
                  <>
                    <div className='flex items-center gap-2'>
                      <StatusDot status={status.ttyd ? 'running' : 'stopped'} />
                      <span className='text-gray-400'>ttyd:</span>
                      <span className={status.ttyd ? 'text-emerald-400' : 'text-gray-500'}>
                        {status.ttyd ? t('localcodeLite.ttydRunning', { port: status.port }) : 'down'}
                      </span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <StatusDot status={status.running ? 'running' : 'stopped'} />
                      <span className='text-gray-400'>session:</span>
                      <span className={status.running ? 'text-emerald-400' : 'text-gray-500'}>
                        {status.running ? t('localcodeLite.sessionActive') : (status.session ? t('localcodeLite.sessionReady') : t('localcodeLite.sessionNone'))}
                      </span>
                    </div>
                  </>
                )
                : <span className='text-red-400'>{t('localcodeLite.statusUnavailable')}</span>}
            </div>

            <div className='flex items-center gap-2'>
              <Button
                variant='primary'
                size='sm'
                onClick={() => startMut.mutate()}
                loading={startMut.isPending}
                disabled={!!status?.running || startMut.isPending}
              >
                <Play size={12} /> {t('localcodeLite.start')}
              </Button>
              <Button
                variant='secondary'
                size='sm'
                onClick={() => stopMut.mutate()}
                loading={stopMut.isPending}
                disabled={!status?.running || stopMut.isPending}
                title={t('localcodeLite.softStopTitle')}
              >
                <Square size={12} /> {t('localcodeLite.stop')}
              </Button>
              <Button
                variant='danger'
                size='sm'
                onClick={async () => {
                  if (await themedConfirm(t('localcodeLite.killConfirm'))) {
                    killMut.mutate();
                  }
                }}
                loading={killMut.isPending}
                disabled={(!status?.session && !status?.ttyd) || killMut.isPending}
                title={t('localcodeLite.hardKillTitle')}
              >
                <Power size={12} /> Kill
              </Button>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => qc.invalidateQueries({ queryKey: ['localcode-lite-status'] })}
                title='Refresh status'
              >
                <RefreshCw size={12} />
              </Button>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => setSettingsOpen(true)}
                title={t('localcodeLite.configTitle')}
              >
                <Settings2 size={14} />
              </Button>
              {status?.port && (
                <a
                  href={`/term/localcode-lite/qwen/`}
                  target='_blank'
                  rel='noopener'
                  className='text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 ml-2'
                  title={t('localcodeLite.openTerminalTitle')}
                >
                  <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Terminal — zabere zbytek viewportu */}
      <Card className='flex-1 flex flex-col min-h-0'>
        <CardContent className='flex-1 p-0 min-h-0'>
          {status?.ttyd
            ? (
              <iframe
                src='/term/localcode-lite/qwen/'
                className='w-full h-full min-h-[600px] border-0 rounded bg-black'
                title='Qwen ttyd'
              />
            )
            : (
              <div className='text-xs text-gray-500 p-8 text-center'>
                {t('localcodeLite.ttydUnavailable')}
              </div>
            )}
        </CardContent>
      </Card>

      {/* Settings modal */}
      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}

function SettingsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const configQ = useQuery({
    queryKey: ['localcode-lite-config'],
    queryFn: () => mcpCall<ConfigResp>('localcode-lite.config_get', {}, { silent: true }),
    staleTime: 30_000,
    enabled: open,
  });

  const updateMut = useMutation({
    mutationFn: () => mcpCall<{ ok: boolean; version?: string; error?: string }>('localcode-lite.qwen_update'),
    onSuccess: (r) => {
      if (r.ok) toast.success(t('localcodeLite.updatedToVersion', { version: r.version ?? '?' }));
      else toast.error(r.error ?? t('localcodeLite.updateFailed'));
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const [form, setForm] = useState<{ qwen_api_base: string; qwen_model: string; qwen_api_key: string }>({
    qwen_api_base: '',
    qwen_model: '',
    qwen_api_key: '',
  });

  // Init form ze server config jakmile dorazí
  if (configQ.data?.config && !form.qwen_api_base && !form.qwen_model) {
    setForm({
      qwen_api_base: configQ.data.config.qwen_api_base ?? '',
      qwen_model: configQ.data.config.qwen_model ?? '',
      qwen_api_key: configQ.data.config.qwen_api_key ?? '',
    });
  }

  const saveMut = useMutation({
    mutationFn: (body: typeof form) => mcpCall('localcode-lite.config_set', body),
    onSuccess: () => {
      toast.success(t('localcodeLite.configSaved'));
      qc.invalidateQueries({ queryKey: ['localcode-lite-config'] });
      onClose();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <Modal open={open} onClose={onClose} title={t('localcodeLite.qwenConfig')}>
      <div className='space-y-3'>
        <div>
          <label className='text-[10px] text-gray-500 mb-1 block'>API Base URL (ACM gateway)</label>
          <input
            type='text'
            value={form.qwen_api_base}
            onChange={(e) => setForm((f) => ({ ...f, qwen_api_base: e.target.value }))}
            placeholder='https://501ef637.llm.services.maxcloud.cz/apiext/llm-profiles/v1'
            className='w-full bg-gray-950 border border-gray-700 rounded px-3 py-2 text-sm text-white font-mono'
          />
        </div>
        <div>
          <label className='text-[10px] text-gray-500 mb-1 block'>Model ID</label>
          <input
            type='text'
            value={form.qwen_model}
            onChange={(e) => setForm((f) => ({ ...f, qwen_model: e.target.value }))}
            placeholder='qwen36-35b'
            className='w-full bg-gray-950 border border-gray-700 rounded px-3 py-2 text-sm text-white font-mono'
          />
        </div>
        <div>
          <label className='text-[10px] text-gray-500 mb-1 block'>
            Bearer token (sk-acm-…)
          </label>
          <input
            type='password'
            value={form.qwen_api_key}
            onChange={(e) => setForm((f) => ({ ...f, qwen_api_key: e.target.value }))}
            placeholder='sk-acm-…'
            className='w-full bg-gray-950 border border-gray-700 rounded px-3 py-2 text-sm text-white font-mono'
          />
        </div>
        <div className='flex justify-end gap-2 pt-2 border-t border-gray-800'>
          <Button variant='secondary' size='sm' onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant='primary'
            size='sm'
            onClick={() => saveMut.mutate(form)}
            loading={saveMut.isPending}
          >
            {t('common.save')}
          </Button>
        </div>

        <div className='pt-3 border-t border-gray-800 space-y-2'>
          <div className='flex items-center justify-between'>
            <div>
              <div className='text-xs text-gray-300'>{t('localcodeLite.updateQwenCode')}</div>
              <div className='text-[10px] text-gray-600'>
                npm install -g @qwen-code/qwen-code@latest
              </div>
            </div>
            <Button
              variant='secondary'
              size='sm'
              onClick={() => updateMut.mutate()}
              loading={updateMut.isPending}
            >
              {t('localcodeLite.update')}
            </Button>
          </div>
        </div>

        <div className='text-[10px] text-gray-600 pt-2 border-t border-gray-800'>
          {t('localcodeLite.configChangeHint')}
        </div>
      </div>
    </Modal>
  );
}
